import { Dispatch, SetStateAction, createContext, useContext, useState } from 'react';
import { ComboboxProps } from '@anyui/core';
import { SelectOption } from '@anytag/shared-types';
import { MultipleSelectProps, SelectProps, SingleSelectProps } from './types';

interface ContextType<TOption extends SelectOption<string>> {
  // common props
  selectProps: SelectProps<TOption>;
  comboboxProps: ComboboxProps;
  // filter state
  search: string;
  setSearch: (search: string) => void;
  // submenu hover state
  hoveringOption: string | null;
  setHoveringOption: Dispatch<SetStateAction<string | null>>;
}

const SelectContext = createContext<ContextType<SelectOption<string>> | null>(null);

interface Props<TOption extends SelectOption<string>> {
  children: React.ReactNode;
  selectProps: SelectProps<TOption>;
}

export const SelectProvider = <TOption extends SelectOption<string>>({ children, selectProps }: Props<TOption>) => {
  const [search, setSearch] = useState('');
  const [hoveringOption, setHoveringOption] = useState<string | null>(null);

  const { invalid, disabled, className, classNames, onSearch } = selectProps;

  return (
    <SelectContext.Provider
      value={{
        search,
        setSearch,
        hoveringOption,
        setHoveringOption,
        selectProps,
        // Disable cmdk's internal sort/filter when onSearch is provided
        // This is avoid reodering options by cmdk when filtering
        comboboxProps: { invalid, disabled, className, classNames, shouldFilter: !onSearch }
      }}
    >
      {children}
    </SelectContext.Provider>
  );
};

export const useSelectContext = <TOption extends SelectOption<string>>() => {
  const context = useContext(SelectContext) as ContextType<TOption>;

  if (!context) {
    throw new Error('Select components must be used within the Select provider');
  }

  return context;
};

// Only used to custom useSelectContext, DON'T PUT ANY LOGIC HERE
export const useSingleSelectContext = <TOption extends SelectOption<string>>() => {
  const context = useSelectContext<TOption>();

  return {
    ...context,
    multiple: false,
    createPrefix: '__create__:',
    selectProps: context.selectProps as SingleSelectProps<TOption>
  };
};

// Only used to custom useSelectContext, DON'T PUT ANY LOGIC HERE
export const useMultipleSelectContext = <TOption extends SelectOption<string>>() => {
  const context = useSelectContext<TOption>();

  return {
    ...context,
    multiple: true,
    selectAllValue: 'select-all' as const,
    selectProps: context.selectProps as MultipleSelectProps<TOption>
  };
};
